import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { updateUserAccount } from "../../../../redux/slices/userSessionSlice";
import { fetchUserPhoto, uploadUserPhoto } from "../../../../redux/slices/userPhotoSlice";
import { getServerUrl } from "../../../../Utils/serverSelector";

const formatDate = (dateStr, language) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "-";

  const localeMap = { ru: "ru-RU", he: "he-IL", en: "en-US" };
  const locale = localeMap[language] || "en-US";

  const result = date.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return result.charAt(0).toUpperCase() + result.slice(1);
};

const ClientProfile = () => {
  const [notification, setNotification] = useState(null);
  const [reloadKey, setReloadKey] = useState(Date.now());
  const fileInputRef = useRef(null);

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const client = useSelector(s => s.userSession?.currentAccount);
  const updateLoading = useSelector(s => s.userSession?.updateLoading);
  const photoPath = useSelector(s => s.photouser?.photoPath);
  const photoLoading = useSelector(s => s.photouser?.status === 'loading');
  const allAppointments = useSelector(s => s.appointments.allAppointments);

  // Загружаем фото при монтировании
  useEffect(() => {
    if (client?.uid) {
      dispatch(fetchUserPhoto(client.uid));
    }
  }, [client?.uid, dispatch]);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    birthDate: "",
    preferredTime: "",
    preferredDays: "",
    reminderMethods: ""
  });

  useEffect(() => {
    if (client) {
      setFormData({
        firstName: client.firstName || "",
        lastName: client.lastName || "",
        phone: client.phone || "",
        email: client.email || "",
        birthDate: client.birthDate || "",
        preferredTime: client.preferredTime || "",
        preferredDays: client.preferredDays || "",
        reminderMethods: client.reminderMethods || ""
      });
    }
  }, [client]);

  // ── Фото ─────────────────────────────────────────────────────────
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || !client?.uid) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('invalid_file_type') || 'Допустимые форматы: JPG, PNG, WEBP');
      return;
    }

    const formData = new FormData();
    formData.append('profilePhoto', file);

    dispatch(uploadUserPhoto({ uid: client.uid, formData }))
      .unwrap()
      .then(() => {
        dispatch(fetchUserPhoto(client.uid));
        setReloadKey(Date.now());
        showNotification(t("photo_updated") || "Фото обновлено", "success");
      })
      .catch(() => showNotification(t("photo_update_error") || "Ошибка загрузки фото", "error"));

    // сбрасываем input чтобы можно было загрузить то же фото повторно
    e.target.value = '';
  };

  // Инициалы для fallback если фото нет
  const initials = client
    ? `${client.firstName?.[0] || ''}${client.lastName?.[0] || ''}`.toUpperCase()
    : '?';

  // ── Форма ─────────────────────────────────────────────────────────
  const containsCode = (value) => {
    const pattern = /<[^>]*>|javascript:|onerror|onload|<script|<\/script|eval\(|\bon\w+=/gi;
    return pattern.test(value);
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    if (containsCode(value)) {
      alert(t('no_code_allowed'));
      setFormData(prev => ({ ...prev, [name]: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    if (containsCode(pasted)) {
      e.preventDefault();
      alert(t('no_code_allowed'));
      setFormData(prev => ({ ...prev, [e.target.name]: '' }));
    }
  };

  const handleSave = () => {
    if (!client?.uid) { alert("user_not_found"); return; }
    if (!formData.firstName.trim()) { alert(t("name_required")); return; }

    const updates = { ...formData };
    if (!updates.birthDate || updates.birthDate === 'Invalid date' || isNaN(new Date(updates.birthDate).getTime())) {
      delete updates.birthDate;
    }

    dispatch(updateUserAccount({ uid: client.uid, updates }))
      .unwrap()
      .then(() => {
        showNotification(t("profile_updated"), "success");
        setIsEditing(false);
      })
      .catch(() => showNotification(t("profile_update_error"), "error"));
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      firstName: client?.firstName || "",
      lastName: client?.lastName || "",
      phone: client?.phone || "",
      email: client?.email || "",
      birthDate: client?.birthDate || "",
      preferredTime: client?.preferredTime || "",
      preferredDays: client?.preferredDays || "",
      reminderMethods: client?.reminderMethods || ""
    });
  };


  // ── Stats ─────────────────────────────────────────────────────────
  const clientPhone = client?.phone || null;

  const totalVisitsByPhone = useMemo(() => {
    if (!clientPhone) return 0;
    return allAppointments.filter(a => a?.phoneContact === clientPhone).length;
  }, [allAppointments, clientPhone]);

  const averageBillByPhone = useMemo(() => {
    if (!clientPhone) return 0;
    const apps = allAppointments.filter(a => a?.phoneContact === clientPhone);
    if (!apps.length) return 0;
    const total = apps.reduce((sum, a) =>
      sum + (a.selectedServices || []).reduce((s, svc) => s + parseFloat(svc?.price || 0), 0), 0);
    return Math.round(total / apps.length);
  }, [allAppointments, clientPhone]);

  const lastAppointmentDate = useMemo(() => {
    if (!clientPhone) return null;
    const apps = allAppointments
      .filter(a => a?.phoneContact === clientPhone)
      .sort((a, b) => new Date(b.dateOfAppointment) - new Date(a.dateOfAppointment));
    return apps[0]?.dateOfAppointment || null;
  }, [allAppointments, clientPhone]);

  const mostFrequentService = useMemo(() => {
    if (!clientPhone) return "-";
    const count = {};
    allAppointments.filter(a => a?.phoneContact === clientPhone).forEach(a => {
      (a.selectedServices || []).forEach(svc => {
        if (svc?.serviceName) count[svc.serviceName] = (count[svc.serviceName] || 0) + 1;
      });
    });
    return Object.entries(count).sort((a, b) => b[1] - a[1])[0]?.[0] || "-";
  }, [allAppointments, clientPhone]);

  const averageDurationByPhone = useMemo(() => {
    if (!clientPhone) return 0;
    const apps = allAppointments.filter(a => a?.phoneContact === clientPhone);
    if (!apps.length) return 0;
    const totalMin = apps.reduce((sum, a) =>
      sum + (a.selectedServices || []).reduce((s, svc) => {
        const dur = typeof svc?.duration === "string"
          ? svc.duration
          : typeof svc?.selectedDuration === "string"
            ? svc.selectedDuration
            : "00:00";
        const [h, m] = dur.split(":").map(Number);
        return s + h * 60 + m;
      }, 0), 0);
    return Math.round(totalMin / apps.length);
  }, [allAppointments, clientPhone]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  if (!client) return null;

  const saveBtnLabel = updateLoading ? (t("saving") || "Сохранение...") : t("save");

  return (
    <div className="profile-container">
      <div className="profile-header">

        {/* ── Аватар ── */}
        <div className="cp-avatar-wrap" onClick={handleAvatarClick}>
          {photoLoading ? (
            <div className="cp-avatar-loading">
              <div className="cp-avatar-spinner" />
            </div>
          ) : photoPath ? (
            <img
              className="cp-avatar-img"
              src={`${getServerUrl()}${photoPath}?t=${reloadKey}`}
              alt={`${client.firstName} ${client.lastName}`}
            />
          ) : (
            <div className="cp-avatar-initials">{initials}</div>
          )}
          <div className="cp-avatar-overlay">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </div>
        </div>

        {/* Скрытый input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <div className="profile-info">
          <h2 className="profile-name">{client.firstName} {client.lastName}</h2>
          <p className="profile-status">
            {t('client_since')} {client.createdAt ? formatDate(client.createdAt, i18n.language) : "-"}
          </p>
        </div>

        {!isEditing ? (
          <button className="btn btn-outline" onClick={() => setIsEditing(true)}>
            {t("editProfile")}
          </button>
        ) : (
          <div className="btns-menu">
            <button className="btn btn-primary" onClick={handleSave} disabled={updateLoading}>
              {saveBtnLabel}
            </button>
            <button className="btn btn-secondary" onClick={handleCancel} disabled={updateLoading}>
              {t("cancel")}
            </button>
          </div>
        )}

        {notification && (
          <div className={`notification-client-profile ${notification.type}`}>
            {notification.message}
          </div>
        )}
      </div>

      <div className="profile-sections">
        <div className="profile-section">
          <h3 className="section-title">{t('personal_information')}</h3>
          <div className="profile-fields">
            {[
              { label: t('first_name'), name: "firstName", type: "text" },
              { label: t('last_name'), name: "lastName", type: "text" },
              { label: t('phone'), name: "phone", type: "text" },
              { label: t('email'), name: "email", type: "email" },
              { label: t('date_of_birth'), name: "birthDate", type: "date" },
            ].map(({ label, name, type }) => (
              <div key={name} className="profile-field">
                <label className="field-label">{label}</label>
                {isEditing ? (
                  <input
                    type={type}
                    name={name}
                    className="search-input"
                    value={formData[name] || ""}
                    onInput={handleInput}
                    onPaste={handlePaste}
                  />
                ) : (
                  <div className="field-value">
                    {name === "birthDate" && client.birthDate
                      ? new Date(client.birthDate).toLocaleDateString()
                      : client[name] || "-"}
                  </div>
                )}
              </div>
            ))}

            {isEditing && (
              <div className="btns-menu-mobile">
                <button className="btn btn-primary" onClick={handleSave} disabled={updateLoading}>
                  {saveBtnLabel}
                </button>
                <button className="btn btn-secondary" onClick={handleCancel} disabled={updateLoading}>
                  {t("cancel")}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="profile-section">
          <h3 className="section-title">{t('statistics')}</h3>
          <div className="stats-grid">
            <div className="stat-card"><div className="stat-value">{totalVisitsByPhone}</div><div className="stat-label">{t('total_visits')}</div></div>
            <div className="stat-card"><div className="stat-value">{client.favoriteMasters || 0}</div><div className="stat-label">{t('favorite_masters')}</div></div>
            <div className="stat-card"><div className="stat-value">{lastAppointmentDate ? formatDate(lastAppointmentDate, i18n.language) : "-"}</div><div className="stat-label">{t('last_visit')}</div></div>
            <div className="stat-card"><div className="stat-value">{mostFrequentService}</div><div className="stat-label">{t('favorite_service')}</div></div>
            <div className="stat-card"><div className="stat-value">{averageDurationByPhone}{t('min')}</div><div className="stat-label">{t('average_visit_time')}</div></div>
            <div className="stat-card"><div className="stat-value">{averageBillByPhone.toLocaleString()} ₪</div><div className="stat-label">{t('average_check')}</div></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientProfile;